import type { PermissionStatus } from "@/caregiver-app/components/onboarding/OnboardingFlow";

const microphonePermissionsModule =
	import.meta.env.VITE_CAPACITOR_BUILD === "true"
		? import("@vcg/capacitor-microphone-permissions")
		: null;

export async function queryMicrophonePermission(): Promise<PermissionStatus> {
	if (!microphonePermissionsModule) return queryWebMicrophonePermission();

	try {
		const { MicrophonePermissions } = await microphonePermissionsModule;
		const { microphone } = await MicrophonePermissions.checkPermissions();
		if (microphone === "granted") return "granted";
		if (microphone === "denied") return "denied";
		return "not-determined";
	} catch {
		return "not-determined";
	}
}

export async function requestMicrophonePermission(): Promise<
	"granted" | "denied"
> {
	if (!microphonePermissionsModule) return requestWebMicrophonePermission();

	try {
		const { MicrophonePermissions } = await microphonePermissionsModule;
		const { microphone } = await MicrophonePermissions.requestPermissions();
		return microphone === "granted" ? "granted" : "denied";
	} catch {
		return "denied";
	}
}

async function queryWebMicrophonePermission(): Promise<PermissionStatus> {
	// Try the Permissions API first (supported in Chromium-based WebViews)
	if (!navigator.permissions?.query) return "not-determined";

	try {
		const result = await navigator.permissions.query({
			name: "microphone" as PermissionName,
		});
		if (result.state === "granted") return "granted";
		if (result.state === "denied") return "denied";
		return "not-determined";
	} catch {
		// Permissions API does not support "microphone" on this platform
		return "not-determined";
	}
}

async function requestWebMicrophonePermission(): Promise<"granted" | "denied"> {
	try {
		const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		for (const track of stream.getTracks()) track.stop();
		return "granted";
	} catch {
		return "denied";
	}
}
